import Modal from "@commons/Modal";
import Heading from "@commons/Typography/Heading";
import Text from "@commons/Typography/Text";
import ListPreview from "@commons/Preview/ListPreview";
import AttachmentPreview from "@commons/Preview/AttachmentPreview";
import { usePage } from "@hooks/usePage";
import { useGetAdditionalDataById } from "@services/individuals/additionalData/getById";
import { formatDateTime } from "@utils/formatters";
import { CircularProgress } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ModalViewAdditionalDataProps {
  individualId: string;
  additionalDataId: string | null;
}

function ModalViewAdditionalData({
  individualId,
  additionalDataId,
}: ModalViewAdditionalDataProps) {
  const { isModalOpen, closeModal } = usePage();
  const open = isModalOpen("viewAdditionalData");

  const {
    data: additionalData,
    isLoading,
    error,
  } = useGetAdditionalDataById(individualId, additionalDataId, {
    refreshInterval: 0,
  });

  const handleClose = () => {
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading="Visualizar Dados Adicionais"
      onClose={handleClose}
      cancelLabel="Fechar"
      className="max-w-4xl"
    >
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <CircularProgress size={40} className="text-primary" />
          <Text size="sm" className="ml-3 text-gray-500">
            Carregando dados...
          </Text>
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center py-8">
          <ErrorOutlineIcon className="text-red-500 mb-2 text-4xl" />
          <Text size="sm" className="text-red-600 text-center">
            Erro ao carregar os dados adicionais. Tente novamente.
          </Text>
        </div>
      )}

      {!isLoading && !error && additionalData && (
        <div className="space-y-6">
          <div>
            <Heading size="sm" className="mb-2 text-gray-700">
              Informações Gerais
            </Heading>
            <ListPreview.Root>
              <ListPreview.Row
                label="Peso (kg):"
                value={
                  additionalData.weightKg
                    ? `${additionalData.weightKg} kg`
                    : "—"
                }
              />
              <ListPreview.Row
                label="Idade estimada (anos):"
                value={
                  additionalData.ageYear
                    ? `${additionalData.ageYear} anos`
                    : "—"
                }
              />
              <ListPreview.Row
                label="Data de registro:"
                value={formatDateTime(additionalData.createdAt) || "—"}
              />
              <ListPreview.Row
                label="Registrado por:"
                value={additionalData.registeredBy || "—"}
              />
            </ListPreview.Root>
          </div>

          {(additionalData.recaptureLatitude ||
            additionalData.recaptureLongitude) && (
            <div>
              <Heading size="sm" className="mb-2 text-gray-700">
                Localização da Captura
              </Heading>
              <ListPreview.Root>
                <ListPreview.Row
                  label="Latitude:"
                  value={
                    additionalData.recaptureLatitude
                      ? additionalData.recaptureLatitude.toString()
                      : "—"
                  }
                />
                <ListPreview.Row
                  label="Longitude:"
                  value={
                    additionalData.recaptureLongitude
                      ? additionalData.recaptureLongitude.toString()
                      : "—"
                  }
                />
              </ListPreview.Root>
            </div>
          )}

          {additionalData.observations && (
            <div>
              <Heading size="sm" className="mb-2 text-gray-700">
                Observações
              </Heading>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <Text size="sm" className="text-gray-800 whitespace-pre-wrap">
                  {additionalData.observations}
                </Text>
              </div>
            </div>
          )}

          <div>
            <Heading size="sm" className="mb-2 text-gray-700">
              Anexos
              {additionalData.attachments &&
              additionalData.attachments.length > 0
                ? ` (${additionalData.attachments.length})`
                : ""}
            </Heading>
            {additionalData.attachments &&
            additionalData.attachments.length > 0 ? (
              <AttachmentPreview
                attachments={additionalData.attachments}
                size="md"
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-center">
                <Text size="sm" className="text-gray-500">
                  Nenhum anexo encontrado.
                </Text>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ModalViewAdditionalData;
